import { useState, useEffect, useRef, useMemo } from 'react';
import { ref, get, set } from 'firebase/database';
import { HiOutlineTrendingUp, HiOutlineTrendingDown, HiStar, HiOutlineTrash } from 'react-icons/hi';
import { db } from '../firebase';

function getFavouritesRef(uid) {
  return uid ? `Users/${uid}/favourites` : null;
}

const WORKSPACE_REF = 'TSS-Report-Workspace';
const ANALYSIS_REF = 'TSS-Analysis';
const PUBLISH_REF = 'TSS-Report-Publish';
const ANALYZE_WATCHLISTS_REF = 'TSS-Config/analyze_watchlists';
const WATCHLIST_DISABLED = 'DISABLED';

const PUBLISH_META_KEYS = ['timestamp', 'publishedAt', 'published_at', 'date'];

function timestampToMs(timestamp) {
  if (timestamp == null || timestamp === '') return null;
  let ms = null;
  if (typeof timestamp === 'number') {
    ms = timestamp < 1e12 ? timestamp * 1000 : timestamp;
  } else if (typeof timestamp === 'string') {
    const s = timestamp.trim().replace(/\+00:00Z$/i, 'Z');
    const parsed = Date.parse(s);
    ms = Number.isNaN(parsed) ? Number(timestamp) : parsed;
    if (typeof ms === 'number' && !Number.isNaN(ms) && ms < 1e12 && ms > 0) ms = ms * 1000;
  } else if (typeof timestamp === 'object' && timestamp !== null) {
    const sec = timestamp.seconds ?? timestamp._seconds ?? timestamp.sec;
    if (typeof sec === 'number') ms = sec * 1000;
  }
  return ms != null && !Number.isNaN(ms) && ms > 0 ? ms : null;
}

function formatPublishedDate(timestamp) {
  const ms = timestampToMs(timestamp);
  if (ms == null) return null;
  const now = Date.now();
  const diffMs = now - ms;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);
  const diffWeek = Math.floor(diffDay / 7);

  if (diffMs < 0) {
    return new Date(ms).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  }
  if (diffSec < 60) return diffSec <= 1 ? 'just now' : `${diffSec} seconds ago`;
  if (diffMin < 60) return diffMin === 1 ? '1 minute ago' : `${diffMin} minutes ago`;
  if (diffHr < 24) return diffHr === 1 ? '1 hour ago' : `${diffHr} hours ago`;
  if (diffDay < 2) return 'yesterday';
  if (diffDay < 7) return `${diffDay} days ago`;
  if (diffWeek < 4) return diffWeek === 1 ? '1 week ago' : `${diffWeek} weeks ago`;
  return new Date(ms).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

function healthClass(health) {
  if (!health || typeof health !== 'string') return '';
  const h = health.toLowerCase();
  if (h === 'good') return 'analysis-health-good';
  if (h === 'bad') return 'analysis-health-bad';
  return 'analysis-health-neutral';
}

function buildReportRow(ticker, score, analysis) {
  if (!analysis || typeof analysis !== 'object') {
    return { Ticker: ticker, ReportScore: score };
  }
  return { ...analysis, Ticker: analysis.Ticker ?? ticker, ReportScore: score };
}

function ReportCard({ row, variant, isFavourited }) {
  const ticker = row.Ticker ?? row.ticker ?? '—';
  const score = row.ReportScore;
  const scoreStr = score != null ? (typeof score === 'number' ? score.toFixed(1) : String(score)) : '—';
  return (
    <article className={`report-card report-card--${variant}`}>
      <div className="report-card-header">
        <div className="report-card-ticker-wrap">
          <span className="report-card-ticker-line">
            <span className="report-card-ticker">{ticker}</span>
            {isFavourited && (
              <HiStar className="report-card-fav" aria-label="Favourited" title="In your favourites" />
            )}
          </span>
          <span className={`report-card-score report-card-score--${variant}`} aria-label={`Score ${scoreStr}`}>
            {scoreStr}
          </span>
        </div>
      </div>
      <p className="report-card-company">{row.CompanyName ?? '—'}</p>
      <dl className="report-card-metrics">
        <div className="report-card-metric">
          <dt>Price</dt>
          <dd className="report-card-value">
            {row.Price != null ? (typeof row.Price === 'number' ? row.Price.toFixed(2) : row.Price) : '—'}
          </dd>
        </div>
        <div className="report-card-metric">
          <dt>P/E</dt>
          <dd className={`report-card-value ${healthClass(row.PERatioHealth)}`}>
            {row.PERatio != null ? (typeof row.PERatio === 'number' ? row.PERatio.toFixed(2) : row.PERatio) : '—'}
          </dd>
        </div>
        <div className="report-card-metric">
          <dt>Signal</dt>
          <dd className={`report-card-value ${healthClass(row.SignalHealth)}`}>{row.Signal ?? '—'}</dd>
        </div>
        <div className="report-card-metric">
          <dt>Analyst</dt>
          <dd className={`report-card-value ${healthClass(row.AnalystHealth)}`}>{row.AnalystRating ?? '—'}</dd>
        </div>
        <div className="report-card-metric">
          <dt>Trend</dt>
          <dd className={`report-card-value ${healthClass(row.TrendHealth)}`}>{row.Trend ?? '—'}</dd>
        </div>
      </dl>
    </article>
  );
}

function getScore(row) {
  const s = row.ReportScore;
  return typeof s === 'number' && !Number.isNaN(s) ? s : (s != null ? Number(s) : NaN);
}

function GroupReportSection({ groupCode, data, favourites, isAdmin, onDeleteGroup }) {
  const rawTop = data?.top ?? [];
  const rawBottom = data?.bottom ?? [];
  const groupFavs = favourites && typeof favourites === 'object' ? favourites : {};
  const top = [...rawTop].sort((a, b) => {
    const sa = getScore(a);
    const sb = getScore(b);
    if (Number.isNaN(sa) && Number.isNaN(sb)) return 0;
    if (Number.isNaN(sa)) return 1;
    if (Number.isNaN(sb)) return -1;
    return sb - sa;
  });
  const bottom = [...rawBottom].sort((a, b) => {
    const sa = getScore(a);
    const sb = getScore(b);
    if (Number.isNaN(sa) && Number.isNaN(sb)) return 0;
    if (Number.isNaN(sa)) return 1;
    if (Number.isNaN(sb)) return -1;
    return sa - sb;
  });
  const hasAny = top.length > 0 || bottom.length > 0;
  if (!hasAny) return null;

  return (
    <div className="daily-report-country-panel">
      {top.length > 0 && (
        <section className="daily-report-subsection daily-report-subsection--buy" aria-labelledby={`report-buy-title-${groupCode}`}>
          <div className="daily-report-subsection-header">
            <h3 id={`report-buy-title-${groupCode}`} className="daily-report-subsection-title">
              <HiOutlineTrendingUp className="daily-report-subsection-icon" aria-hidden />
              Recommended to buy
            </h3>
            <div className="daily-report-subsection-header-right">
              <span className="daily-report-subsection-count">{top.length} stock{top.length !== 1 ? 's' : ''}</span>
              {isAdmin && typeof onDeleteGroup === 'function' && (
                <button
                  type="button"
                  className="btn btn-muted btn-xs daily-report-subsection-delete"
                  onClick={() => onDeleteGroup(groupCode)}
                >
                  Delete report
                </button>
              )}
            </div>
          </div>
          <p className="daily-report-subsection-desc">Stocks with strongest buy signals.</p>
          <div className="report-card-grid">
            {top.map((row) => {
              const ticker = row.Ticker ?? row.ticker;
              return (
                <ReportCard
                  key={ticker}
                  row={row}
                  variant="buy"
                  isFavourited={groupFavs[ticker] === true}
                />
              );
            })}
          </div>
        </section>
      )}

      {bottom.length > 0 && (
        <section className="daily-report-subsection daily-report-subsection--sell" aria-labelledby={`report-sell-title-${groupCode}`}>
          <div className="daily-report-subsection-header">
            <h3 id={`report-sell-title-${groupCode}`} className="daily-report-subsection-title">
              <HiOutlineTrendingDown className="daily-report-subsection-icon" aria-hidden />
              Recommended to sell
            </h3>
            <span className="daily-report-subsection-count">{bottom.length} stock{bottom.length !== 1 ? 's' : ''}</span>
          </div>
          <p className="daily-report-subsection-desc">Consider reducing exposure.</p>
          <div className="report-card-grid">
            {bottom.map((row) => {
              const ticker = row.Ticker ?? row.ticker;
              return (
                <ReportCard
                  key={ticker}
                  row={row}
                  variant="sell"
                  isFavourited={groupFavs[ticker] === true}
                />
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}

function DailyReport({ isAdmin, user }) {
  const [reportData, setReportData] = useState(null);
  const [analyzeWatchlists, setAnalyzeWatchlists] = useState(null);
  const [publishedTimestamp, setPublishedTimestamp] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toasts, setToasts] = useState([]);
  const toastTimeoutsRef = useRef([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [favouritesByGroup, setFavouritesByGroup] = useState({});

  const showToast = (message) => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message }]);
    const t = window.setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, 3000);
    toastTimeoutsRef.current.push(t);
  };

  useEffect(() => {
    return () => {
      toastTimeoutsRef.current.forEach(clearTimeout);
      toastTimeoutsRef.current = [];
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [publishSnap, watchlistsSnap] = await Promise.all([
          get(ref(db, PUBLISH_REF)),
          get(ref(db, ANALYZE_WATCHLISTS_REF)),
        ]);
        if (cancelled) return;
        const raw = typeof publishSnap.val() === 'object' && publishSnap.val() !== null ? publishSnap.val() : {};
        const watchlists = typeof watchlistsSnap.val() === 'object' && watchlistsSnap.val() !== null ? watchlistsSnap.val() : {};
        setPublishedTimestamp(raw.timestamp ?? raw.publishedAt ?? raw.published_at ?? raw.date ?? null);
        const payload = { ...raw };
        PUBLISH_META_KEYS.forEach((k) => delete payload[k]);
        setReportData(payload);
        setAnalyzeWatchlists(watchlists);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load report');
        }
      } finally {
        if (!cancelled) {
          setInitialLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const uid = user?.uid;
    if (!uid) {
      setFavouritesByGroup({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const favRef = getFavouritesRef(uid);
        if (!favRef) return;
        const snapshot = await get(ref(db, favRef));
        const data = snapshot.val();
        if (!cancelled) {
          setFavouritesByGroup(typeof data === 'object' && data !== null ? data : {});
        }
      } catch {
        if (!cancelled) setFavouritesByGroup({});
      }
    })();
    return () => { cancelled = true; };
  }, [user?.uid]);

  const watchlistCodes = useMemo(() => {
    if (!reportData || !analyzeWatchlists) return [];
    const exclude = new Set(['last_email_sent_at']);
    const allKeys = Object.keys(reportData)
      .filter((k) => !exclude.has(k))
      .filter((k) => analyzeWatchlists[k] != null && analyzeWatchlists[k] !== '' && analyzeWatchlists[k] !== WATCHLIST_DISABLED && typeof analyzeWatchlists[k] === 'string')
      .sort((a, b) => a.localeCompare(b));
    const snp500Key = allKeys.find((k) => k === 'snp500');
    return snp500Key ? [snp500Key, ...allKeys.filter((c) => c !== snp500Key)] : allKeys;
  }, [reportData, analyzeWatchlists]);

  useEffect(() => {
    if (watchlistCodes.length > 0 && selectedGroup === null) {
      setSelectedGroup(watchlistCodes[0]);
    }
    if (watchlistCodes.length > 0 && selectedGroup && !watchlistCodes.includes(selectedGroup)) {
      setSelectedGroup(watchlistCodes[0]);
    }
  }, [watchlistCodes, selectedGroup]);

  const handleSeeIncompleteReport = async () => {
    setError(null);
    setLoading(true);
    try {
      const [workspaceSnap, analysisSnap] = await Promise.all([
        get(ref(db, WORKSPACE_REF)),
        get(ref(db, ANALYSIS_REF)),
      ]);
      const workspace = workspaceSnap.val();
      const analysis = analysisSnap.val();
      const workspaceByGroup = typeof workspace === 'object' && workspace !== null ? workspace : {};
      const analysisByGroup = typeof analysis === 'object' && analysis !== null ? analysis : {};

      const publish = {};
      for (const group of Object.keys(workspaceByGroup)) {
        const groupWorkspace = workspaceByGroup[group];
        const groupAnalysis = analysisByGroup[group] ?? {};
        const topTickers = groupWorkspace?.top && typeof groupWorkspace.top === 'object' ? groupWorkspace.top : {};
        const bottomTickers = groupWorkspace?.bottom && typeof groupWorkspace.bottom === 'object' ? groupWorkspace.bottom : {};

        const top = Object.entries(topTickers).map(([ticker, score]) =>
          buildReportRow(ticker, score, groupAnalysis[ticker])
        );
        const bottom = Object.entries(bottomTickers).map(([ticker, score]) =>
          buildReportRow(ticker, score, groupAnalysis[ticker])
        );

        publish[group] = { top, bottom };
      }

      for (const group of Object.keys(publish)) {
        await set(ref(db, `${PUBLISH_REF}/${group}`), publish[group]);
      }

      const timestampValue = new Date().toISOString();
      await set(ref(db, `${PUBLISH_REF}/timestamp`), timestampValue);
      setPublishedTimestamp(timestampValue);
      setReportData(publish);
      showToast('Incomplete report generated');
    } catch (err) {
      setError(err.message || 'Failed to build report');
    } finally {
      setLoading(false);
    }
  };

  const publishedLabel = formatPublishedDate(publishedTimestamp);
  const handleDeleteAllReports = async () => {
    if (!isAdmin) return;
    if (!window.confirm('Delete all published report data for all watchlists? This cannot be undone.')) return;
    setError(null);
    setLoading(true);
    try {
      await set(ref(db, PUBLISH_REF), null);
      setReportData(null);
      setPublishedTimestamp(null);
      showToast('All reports deleted');
    } catch (err) {
      setError(err.message || 'Failed to delete reports');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReportForGroup = async (groupCode) => {
    if (!isAdmin || !groupCode) return;
    if (!window.confirm(`Delete report for ${groupCode}? This cannot be undone.`)) return;
    setError(null);
    setLoading(true);
    try {
      await set(ref(db, `${PUBLISH_REF}/${groupCode}`), null);
      setReportData((prev) => {
        if (!prev) return prev;
        const next = { ...prev };
        delete next[groupCode];
        return next;
      });
      showToast(`Report for ${groupCode} deleted`);
    } catch (err) {
      setError(err.message || 'Failed to delete report');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="page page--full-width daily-report-page-wrap">
        <div className="daily-report-page">
          <header className="daily-report-header">
            <h1 className="daily-report-title">Daily Report</h1>
          </header>
          <p className="daily-report-placeholder">Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page page--full-width daily-report-page-wrap">
      <div className="toast-container" role="status" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className="toast">
            {toast.message}
          </div>
        ))}
      </div>

      <div className="daily-report-page">
        <header className="daily-report-header">
          <h1 className="daily-report-title">Daily Report</h1>
          {publishedLabel && (
            <span className="daily-report-badge-published" title={`Report was published ${publishedLabel}`}>
              {publishedLabel}
            </span>
          )}
          {isAdmin && (
            <button
              type="button"
              className="btn btn-muted daily-report-delete-btn"
              onClick={handleDeleteAllReports}
              disabled={loading}
            >
              <HiOutlineTrash aria-hidden style={{ width: '0.9rem', height: '0.9rem', marginRight: '0.25rem' }} />
              Delete all reports
            </button>
          )}
        </header>

        {error && (
          <p className="daily-report-error" role="alert">
            {error}
          </p>
        )}

        {reportData && watchlistCodes.length === 0 && !error && isAdmin && (
          <div className="daily-report-empty">
          <p className="daily-report-empty-title">No report yet</p>
          <p className="daily-report-empty-desc">
            Generate a report from your current workspace and analysis data below.
          </p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSeeIncompleteReport}
            disabled={loading}
          >
            {loading ? 'Generating…' : 'Generate incomplete report'}
          </button>
          </div>
        )}

        {reportData && watchlistCodes.length > 0 && (
          <div className="daily-report-content">
          <nav className="daily-report-tabs" role="tablist" aria-label="Watchlist">
            {watchlistCodes.map((code) => (
              <button
                key={code}
                type="button"
                role="tab"
                aria-selected={selectedGroup === code}
                aria-controls={`report-panel-${code}`}
                id={`report-tab-${code}`}
                className={`daily-report-tab ${selectedGroup === code ? 'daily-report-tab--active' : ''}`}
                onClick={() => setSelectedGroup(code)}
              >
                {code}
              </button>
            ))}
          </nav>

          {watchlistCodes.map((code) => (
            <div
              key={code}
              id={`report-panel-${code}`}
              role="tabpanel"
              aria-labelledby={`report-tab-${code}`}
              hidden={selectedGroup !== code}
              className="daily-report-panel"
            >
              {selectedGroup === code && (
                <GroupReportSection
                  groupCode={code}
                  data={reportData[code]}
                  favourites={favouritesByGroup[code]}
                  isAdmin={isAdmin}
                  onDeleteGroup={handleDeleteReportForGroup}
                />
              )}
            </div>
          ))}
          </div>
        )}

      {reportData && isAdmin && (
          <div className="daily-report-actions">
            <p className="daily-report-warning" role="status">
              The report may not be accurate when generated from incomplete data.
            </p>
            <button
              type="button"
              className="btn btn-muted"
              onClick={handleSeeIncompleteReport}
              disabled={loading}
            >
              {loading ? 'Generating…' : 'Generate incomplete report'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default DailyReport;
